import React, { useState } from 'react';

export const ExperienceTab = ({ experience, setExperience }) => {
  const handleAdd = () => {
    setExperience([...experience, {
      jobTitle: '',
      company: '',
      location: { city: '', state: '', country: '' },
      startDate: '',
      endDate: '',
      currentlyWorking: false,
      description: '',
      achievements: [''],
      technologies: ['']
    }]);
  };

  const handleRemove = (index) => {
    setExperience(experience.filter((_, i) => i !== index));
  };

  const handleUpdate = (index, field, value) => {
    const updated = experience.map((item, i) => 
      i === index ? { ...item, [field]: value } : item
    );
    setExperience(updated);
  };

  const handleAchievementAdd = (index) => {
    const updated = experience.map((item, i) => 
      i === index ? { ...item, achievements: [...item.achievements, ''] } : item
    );
    setExperience(updated);
  };

  const handleAchievementUpdate = (expIndex, achIndex, value) => {
    const updated = experience.map((item, i) => 
      i === expIndex ? {
        ...item,
        achievements: item.achievements.map((ach, j) => j === achIndex ? value : ach)
      } : item
    );
    setExperience(updated);
  };

  const handleAchievementRemove = (expIndex, achIndex) => {
    const updated = experience.map((item, i) => 
      i === expIndex ? {
        ...item,
        achievements: item.achievements.filter((_, j) => j !== achIndex)
      } : item
    );
    setExperience(updated);
  };

  return (
    <div className="form-section">
      <div className="section-header">
        <h2>Work Experience</h2>
        <button type="button" className="btn-add" onClick={handleAdd}>
          + Add Experience
        </button>
      </div>

      {experience.length === 0 && (
        <div className="empty-message">
          No work experience added yet. Click "Add Experience" to get started.
        </div>
      )}

      {experience.map((exp, index) => (
        <div key={index} className="item-card">
          <div className="card-header">
            <h3>Experience #{index + 1}</h3>
            <button 
              type="button" 
              className="btn-remove"
              onClick={() => handleRemove(index)}
            >
              Remove
            </button>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Job Title *</label>
              <input
                type="text"
                value={exp.jobTitle}
                onChange={(e) => handleUpdate(index, 'jobTitle', e.target.value)}
                placeholder="e.g., Senior Software Engineer"
                required
              />
            </div>
            <div className="form-group">
              <label>Company *</label>
              <input
                type="text"
                value={exp.company}
                onChange={(e) => handleUpdate(index, 'company', e.target.value)}
                placeholder="e.g., Google Inc."
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Start Date *</label>
              <input
                type="month"
                value={exp.startDate ? exp.startDate.substring(0, 7) : ''}
                onChange={(e) => handleUpdate(index, 'startDate', e.target.value + '-01')}
                required
              />
            </div>
            <div className="form-group">
              <label>End Date</label>
              <input
                type="month"
                value={exp.endDate ? exp.endDate.substring(0, 7) : ''}
                onChange={(e) => handleUpdate(index, 'endDate', e.target.value + '-01')}
                disabled={exp.currentlyWorking}
              />
            </div>
            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={exp.currentlyWorking || false}
                  onChange={(e) => {
                    const updated = experience.map((item, i) => 
                      i === index ? { 
                        ...item, 
                        currentlyWorking: e.target.checked,
                        endDate: e.target.checked ? '' : item.endDate
                      } : item
                    );
                    setExperience(updated);
                  }}
                />
                Currently Working
              </label>
            </div>
          </div>

          <div className="form-group full">
            <label>Description</label>
            <textarea
              value={exp.description}
              onChange={(e) => handleUpdate(index, 'description', e.target.value)}
              placeholder="Brief description of your role and responsibilities..."
              rows={3}
            />
          </div>

          <div className="form-group full">
            <label>Key Achievements</label>
            {exp.achievements.map((achievement, achIndex) => (
              <div key={achIndex} className="achievement-row">
                <input
                  type="text"
                  value={achievement}
                  onChange={(e) => handleAchievementUpdate(index, achIndex, e.target.value)}
                  placeholder="e.g., Increased system performance by 40%"
                />
                <button
                  type="button"
                  className="btn-remove-small"
                  onClick={() => handleAchievementRemove(index, achIndex)}
                >
                  ×
                </button>
              </div>
            ))}
            <button 
              type="button" 
              className="btn-add-small"
              onClick={() => handleAchievementAdd(index)}
            >
              + Add Achievement
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export const EducationTab = ({ education, setEducation }) => {
  const handleAdd = () => {
    setEducation([...education, {
      degree: '',
      field: '',
      institution: '',
      location: { city: '', state: '', country: '' },
      startDate: '',
      endDate: '',
      currentlyStudying: false,
      gpa: '',
      honors: ''
    }]);
  };

  const handleRemove = (index) => {
    setEducation(education.filter((_, i) => i !== index));
  };

  const handleUpdate = (index, field, value) => {
    const updated = education.map((item, i) => 
      i === index ? { ...item, [field]: value } : item
    );
    setEducation(updated);
  };

  return (
    <div className="form-section">
      <div className="section-header">
        <h2>Education</h2>
        <button type="button" className="btn-add" onClick={handleAdd}>
          + Add Education
        </button>
      </div>

      {education.length === 0 && (
        <div className="empty-message">
          No education added yet. Click "Add Education" to get started.
        </div>
      )}

      {education.map((edu, index) => (
        <div key={index} className="item-card">
          <div className="card-header">
            <h3>Education #{index + 1}</h3>
            <button 
              type="button" 
              className="btn-remove"
              onClick={() => handleRemove(index)}
            >
              Remove
            </button>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Degree *</label>
              <input
                type="text"
                value={edu.degree}
                onChange={(e) => handleUpdate(index, 'degree', e.target.value)}
                placeholder="e.g., Bachelor of Science"
                required
              />
            </div>
            <div className="form-group">
              <label>Field of Study</label>
              <input
                type="text"
                value={edu.field}
                onChange={(e) => handleUpdate(index, 'field', e.target.value)}
                placeholder="e.g., Computer Science"
              />
            </div>
          </div>

          <div className="form-group full">
            <label>Institution *</label>
            <input
              type="text"
              value={edu.institution}
              onChange={(e) => handleUpdate(index, 'institution', e.target.value)}
              placeholder="e.g., Stanford University"
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Start Date</label>
              <input
                type="month"
                value={edu.startDate ? edu.startDate.substring(0, 7) : ''}
                onChange={(e) => handleUpdate(index, 'startDate', e.target.value + '-01')}
              />
            </div>
            <div className="form-group">
              <label>End Date</label>
              <input
                type="month"
                value={edu.endDate ? edu.endDate.substring(0, 7) : ''}
                onChange={(e) => handleUpdate(index, 'endDate', e.target.value + '-01')}
                disabled={edu.currentlyStudying}
              />
            </div>
            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={edu.currentlyStudying || false}
                  onChange={(e) => {
                    const updated = education.map((item, i) => 
                      i === index ? { 
                        ...item, 
                        currentlyStudying: e.target.checked,
                        endDate: e.target.checked ? '' : item.endDate
                      } : item
                    );
                    setEducation(updated);
                  }}
                />
                Currently Studying
              </label>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>GPA</label>
              <input
                type="text"
                value={edu.gpa}
                onChange={(e) => handleUpdate(index, 'gpa', e.target.value)}
                placeholder="e.g., 3.8/4.0"
              />
            </div>
            <div className="form-group">
              <label>Honors</label>
              <input
                type="text"
                value={edu.honors}
                onChange={(e) => handleUpdate(index, 'honors', e.target.value)}
                placeholder="e.g., Summa Cum Laude"
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export const SkillsTab = ({ skills, setSkills }) => {
  const [newSkill, setNewSkill] = useState({ technical: '', soft: '', other: '' });
  const [newLanguage, setNewLanguage] = useState({ name: '', proficiency: 'Intermediate' });

  const handleSkillAdd = (type) => {
    if (!newSkill[type].trim()) return;
    setSkills({
      ...skills,
      [type]: [...(skills[type] || []), newSkill[type]]
    });
    setNewSkill({ ...newSkill, [type]: '' });
  };

  const handleSkillRemove = (type, index) => {
    setSkills({
      ...skills,
      [type]: skills[type].filter((_, i) => i !== index)
    });
  };

  const handleLanguageAdd = () => {
    if (!newLanguage.name.trim()) return;
    setSkills({
      ...skills,
      languages: [...(skills.languages || []), { ...newLanguage }]
    });
    setNewLanguage({ name: '', proficiency: 'Intermediate' });
  };

  const handleLanguageRemove = (index) => {
    setSkills({
      ...skills,
      languages: skills.languages.filter((_, i) => i !== index)
    });
  };

  return (
    <div className="form-section">
      <h2>Skills</h2>

      <div className="skills-group">
        <h3>Technical Skills</h3>
        <div className="skill-input-row">
          <input
            type="text"
            value={newSkill.technical}
            onChange={(e) => setNewSkill({ ...newSkill, technical: e.target.value })}
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleSkillAdd('technical'))}
            placeholder="e.g., JavaScript, Python, React..."
          />
          <button type="button" onClick={() => handleSkillAdd('technical')}>Add</button>
        </div>
        <div className="skills-list">
          {(skills.technical || []).map((skill, index) => (
            <span key={index} className="skill-tag">
              {skill}
              <button type="button" onClick={() => handleSkillRemove('technical', index)}>×</button>
            </span>
          ))}
        </div>
      </div>

      <div className="skills-group">
        <h3>Soft Skills</h3>
        <div className="skill-input-row">
          <input
            type="text"
            value={newSkill.soft}
            onChange={(e) => setNewSkill({ ...newSkill, soft: e.target.value })}
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleSkillAdd('soft'))}
            placeholder="e.g., Leadership, Communication..."
          />
          <button type="button" onClick={() => handleSkillAdd('soft')}>Add</button>
        </div>
        <div className="skills-list">
          {(skills.soft || []).map((skill, index) => (
            <span key={index} className="skill-tag">
              {skill}
              <button type="button" onClick={() => handleSkillRemove('soft', index)}>×</button>
            </span>
          ))}
        </div>
      </div>

      <div className="skills-group">
        <h3>Languages</h3>
        <div className="language-input-row">
          <input
            type="text"
            value={newLanguage.name}
            onChange={(e) => setNewLanguage({ ...newLanguage, name: e.target.value })}
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleLanguageAdd())}
            placeholder="e.g., English, Spanish..."
          />
          <select
            value={newLanguage.proficiency}
            onChange={(e) => setNewLanguage({ ...newLanguage, proficiency: e.target.value })}
          >
            <option value="Native">Native</option>
            <option value="Fluent">Fluent</option>
            <option value="Professional">Professional</option>
            <option value="Intermediate">Intermediate</option>
            <option value="Basic">Basic</option>
          </select>
          <button type="button" onClick={handleLanguageAdd}>Add</button>
        </div>
        <div className="skills-list">
          {(skills.languages || []).map((lang, index) => (
            <span key={index} className="skill-tag language-tag">
              <span className="language-name">{lang.name}</span>
              <span className="language-proficiency">({lang.proficiency})</span>
              <button type="button" onClick={() => handleLanguageRemove(index)}>×</button>
            </span>
          ))}
        </div>
      </div>

      <div className="skills-group">
        <h3>Other Skills</h3>
        <div className="skill-input-row">
          <input
            type="text"
            value={newSkill.other}
            onChange={(e) => setNewSkill({ ...newSkill, other: e.target.value })}
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleSkillAdd('other'))}
            placeholder="e.g., Project Management, Agile..."
          />
          <button type="button" onClick={() => handleSkillAdd('other')}>Add</button>
        </div>
        <div className="skills-list">
          {(skills.other || []).map((skill, index) => (
            <span key={index} className="skill-tag">
              {skill}
              <button type="button" onClick={() => handleSkillRemove('other', index)}>×</button>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export const ProjectsTab = ({ projects, setProjects }) => {
  const [newTech, setNewTech] = React.useState({});
  const [newHighlight, setNewHighlight] = React.useState({});

  const handleAdd = () => {
    setProjects([...projects, {
      name: '',
      role: '',
      description: '',
      startDate: '',
      endDate: '',
      currentlyWorking: false,
      url: '',
      repository: '',
      technologies: [],
      highlights: []
    }]);
  };

  const handleRemove = (index) => {
    setProjects(projects.filter((_, i) => i !== index));
  };

  const handleUpdate = (index, field, value) => {
    const updated = projects.map((item, i) => 
      i === index ? { ...item, [field]: value } : item
    );
    setProjects(updated);
  };

  const handleHighlightAdd = (projIndex, highlight) => {
    if (!highlight || !highlight.trim()) return;
    const updated = projects.map((item, i) => 
      i === projIndex ? { ...item, highlights: [...(item.highlights || []), highlight] } : item
    );
    setProjects(updated);
    setNewHighlight({ ...newHighlight, [projIndex]: '' });
  };

  const handleHighlightRemove = (projIndex, highlightIndex) => {
    const updated = projects.map((item, i) => 
      i === projIndex ? {
        ...item,
        highlights: item.highlights.filter((_, j) => j !== highlightIndex)
      } : item
    );
    setProjects(updated);
  };

  const handleTechnologyAdd = (projIndex, tech) => {
    if (!tech || !tech.trim()) return;
    const updated = projects.map((item, i) => 
      i === projIndex ? { ...item, technologies: [...(item.technologies || []), tech] } : item
    );
    setProjects(updated);
    setNewTech({ ...newTech, [projIndex]: '' });
  };

  const handleTechnologyRemove = (projIndex, techIndex) => {
    const updated = projects.map((item, i) => 
      i === projIndex ? {
        ...item,
        technologies: item.technologies.filter((_, j) => j !== techIndex)
      } : item
    );
    setProjects(updated);
  };

  return (
    <div className="form-section">
      <div className="section-header">
        <h2>Projects</h2>
        <button type="button" className="btn-add" onClick={handleAdd}>
          + Add Project
        </button>
      </div>

      {projects.length === 0 && (
        <div className="empty-message">
          No projects added yet. Click "Add Project" to showcase your work.
        </div>
      )}

      {projects.map((project, index) => (
        <div key={index} className="item-card">
          <div className="card-header">
            <h3>Project #{index + 1}</h3>
            <button 
              type="button" 
              className="btn-remove"
              onClick={() => handleRemove(index)}
            >
              Remove
            </button>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Project Name *</label>
              <input
                type="text"
                value={project.name}
                onChange={(e) => handleUpdate(index, 'name', e.target.value)}
                placeholder="e.g., E-commerce Platform"
                required
              />
            </div>
            <div className="form-group">
              <label>Your Role</label>
              <input
                type="text"
                value={project.role}
                onChange={(e) => handleUpdate(index, 'role', e.target.value)}
                placeholder="e.g., Lead Developer"
              />
            </div>
          </div>

          <div className="form-group full">
            <label>Description</label>
            <textarea
              value={project.description}
              onChange={(e) => handleUpdate(index, 'description', e.target.value)}
              placeholder="Brief description of the project..."
              rows={3}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Start Date</label>
              <input
                type="month"
                value={project.startDate ? project.startDate.substring(0, 7) : ''}
                onChange={(e) => handleUpdate(index, 'startDate', e.target.value + '-01')}
              />
            </div>
            <div className="form-group">
              <label>End Date</label>
              <input
                type="month"
                value={project.endDate ? project.endDate.substring(0, 7) : ''}
                onChange={(e) => handleUpdate(index, 'endDate', e.target.value + '-01')}
                disabled={project.currentlyWorking}
              />
            </div>
            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={project.currentlyWorking || false}
                  onChange={(e) => {
                    const updated = projects.map((item, i) => 
                      i === index ? { 
                        ...item, 
                        currentlyWorking: e.target.checked,
                        endDate: e.target.checked ? '' : item.endDate
                      } : item
                    );
                    setProjects(updated);
                  }}
                />
                Ongoing Project
              </label>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Project URL</label>
              <input
                type="url"
                value={project.url}
                onChange={(e) => handleUpdate(index, 'url', e.target.value)}
                placeholder="https://project-demo.com"
              />
            </div>
            <div className="form-group">
              <label>Repository</label>
              <input
                type="url"
                value={project.repository}
                onChange={(e) => handleUpdate(index, 'repository', e.target.value)}
                placeholder="https://github.com/username/repo"
              />
            </div>
          </div>

          <div className="form-group full">
            <label>Technologies Used</label>
            <div className="skill-input-row">
              <input
                type="text"
                value={newTech[index] || ''}
                onChange={(e) => setNewTech({ ...newTech, [index]: e.target.value })}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleTechnologyAdd(index, newTech[index]))}
                placeholder="e.g., React, Node.js, MongoDB"
              />
              <button type="button" onClick={() => handleTechnologyAdd(index, newTech[index])}>Add</button>
            </div>
            <div className="skills-list">
              {(project.technologies || []).map((tech, techIndex) => (
                <span key={techIndex} className="skill-tag">
                  {tech}
                  <button type="button" onClick={() => handleTechnologyRemove(index, techIndex)}>×</button>
                </span>
              ))}
            </div>
          </div>

          <div className="form-group full">
            <label>Key Highlights</label>
            <div className="skill-input-row">
              <input
                type="text"
                value={newHighlight[index] || ''}
                onChange={(e) => setNewHighlight({ ...newHighlight, [index]: e.target.value })}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleHighlightAdd(index, newHighlight[index]))}
                placeholder="e.g., Served 10,000+ users in first month"
              />
              <button type="button" onClick={() => handleHighlightAdd(index, newHighlight[index])}>Add</button>
            </div>
            <div className="skills-list">
              {(project.highlights || []).map((highlight, highlightIndex) => (
                <span key={highlightIndex} className="skill-tag">
                  {highlight}
                  <button type="button" onClick={() => handleHighlightRemove(index, highlightIndex)}>×</button>
                </span>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

